export type AgentWorkflowStatus = "draft" | "ready" | "paused" | "archived";
export type AgentWorkflowSource = "chat" | "template" | "import";
export type AgentWorkflowScheduleKind = "manual" | "cron" | "interval";
export type AgentWorkflowRuntimeTarget = "local-codex" | "cloud-codex-runner" | "managed-api";
export type AgentWorkflowExecutionTarget = AgentWorkflowRuntimeTarget;
export type AgentWorkflowExecutionKind = "agent" | "convex";
export type AgentWorkflowDeploymentStatus =
  | "draft"
  | "validating"
  | "approval-required"
  | "approved"
  | "deploying"
  | "deployed"
  | "failed"
  | "retired";
export type AgentWorkflowRunTrigger = "manual" | "scheduled" | "chat";
export type AgentWorkflowRunStatus =
  | "queued"
  | "running"
  | "waiting-for-approval"
  | "completed"
  | "failed"
  | "cancelled";
export type AgentWorkflowStepKind = "agent" | "werkflow-tool" | "approval" | "sandbox";

export const AGENT_WORKFLOW_TENANT_STORE_SCHEMA_VERSION = 1;

export interface AgentWorkflowSchedule {
  readonly kind: AgentWorkflowScheduleKind;
  readonly expression?: string;
  readonly timezone?: string;
  readonly label: string;
}

export interface AgentWorkflowSecurityPolicy {
  readonly runtimeTarget: AgentWorkflowRuntimeTarget;
  readonly sandboxAllowed: boolean;
  readonly allowedToolIds: ReadonlyArray<string>;
  readonly approvalRequiredForCustomerFacingActions: boolean;
  readonly approvalRequiredForWrites: boolean;
  readonly maxRunMinutes: number;
}

export interface AgentWorkflowConvexDefinition {
  readonly deploymentRef: string;
  readonly entrypoint: string;
  readonly sourceVersion: number;
  readonly artifactHash?: string;
  readonly generatedSource?: string;
  readonly deploymentStatus: AgentWorkflowDeploymentStatus;
  readonly deploymentId?: string;
  readonly cronExpressionUtc?: string;
  readonly declaredCapabilities: ReadonlyArray<string>;
  readonly declaredSideEffects: ReadonlyArray<string>;
}

export interface AgentWorkflowStepDefinition {
  readonly id: string;
  readonly kind: AgentWorkflowStepKind;
  readonly title: string;
  readonly instruction: string;
  readonly toolId?: string;
  readonly approvalReason?: string;
}

export interface AgentWorkflowDefinition {
  readonly id: string;
  readonly version: number;
  readonly name: string;
  readonly status: AgentWorkflowStatus;
  readonly source: AgentWorkflowSource;
  readonly naturalLanguageDefinition: string;
  readonly compactSkillDefinition: string;
  readonly schedule: AgentWorkflowSchedule;
  readonly security: AgentWorkflowSecurityPolicy;
  readonly executionKind?: AgentWorkflowExecutionKind;
  readonly convex?: AgentWorkflowConvexDefinition;
  readonly steps: ReadonlyArray<AgentWorkflowStepDefinition>;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AgentWorkflowRunEvent {
  readonly id: string;
  readonly at: string;
  readonly type:
    | "started"
    | "tool-call"
    | "approval-requested"
    | "approval-resolved"
    | "result"
    | "error";
  readonly message: string;
}

export interface AgentWorkflowRunRecord {
  readonly id: string;
  readonly workflowId: string;
  readonly workflowVersion: number;
  readonly status: AgentWorkflowRunStatus;
  readonly trigger?: AgentWorkflowRunTrigger;
  readonly runtimeTarget?: AgentWorkflowRuntimeTarget;
  readonly scheduledFor?: string;
  readonly startedAt?: string;
  readonly completedAt?: string;
  readonly inputSummary: string;
  readonly resultSummary?: string;
  readonly errorSummary?: string;
  readonly approvalRequestId?: string;
  readonly convexWorkflowId?: string;
  readonly convexDeploymentId?: string;
  readonly convexStep?: string;
  readonly retryCount?: number;
  readonly events: ReadonlyArray<AgentWorkflowRunEvent>;
}

export interface AgentWorkflowTenantStore {
  readonly tenantId: string;
  readonly schemaVersion: typeof AGENT_WORKFLOW_TENANT_STORE_SCHEMA_VERSION;
  readonly definitions: ReadonlyArray<AgentWorkflowDefinition>;
  readonly runs: ReadonlyArray<AgentWorkflowRunRecord>;
  readonly updatedAt: string;
}

export interface AgentWorkflowSkillRenderOptions {
  readonly includeSchedule?: boolean;
}

export interface CreateAgentWorkflowTenantStoreOptions {
  readonly tenantId: string;
  readonly updatedAt: string;
  readonly definitions?: ReadonlyArray<AgentWorkflowDefinition>;
  readonly runs?: ReadonlyArray<AgentWorkflowRunRecord>;
}

export interface AgentWorkflowChatCommand {
  readonly actionHint: "create" | "reschedule";
  readonly naturalLanguageDefinition: string;
  readonly name: string;
  readonly schedule: AgentWorkflowSchedule;
}

export interface CreateAgentWorkflowFromChatCommandOptions {
  readonly id: string;
  readonly command: AgentWorkflowChatCommand;
  readonly createdAt: string;
}

export interface CreateAgentWorkflowRunRecordOptions {
  readonly id: string;
  readonly workflow: AgentWorkflowDefinition;
  readonly trigger: AgentWorkflowRunTrigger;
  readonly startedAt: string;
  readonly completedAt?: string;
  readonly scheduledFor?: string;
}

export const DEFAULT_AGENT_WORKFLOW_SECURITY: AgentWorkflowSecurityPolicy = {
  runtimeTarget: "local-codex",
  sandboxAllowed: false,
  allowedToolIds: [],
  approvalRequiredForCustomerFacingActions: true,
  approvalRequiredForWrites: true,
  maxRunMinutes: 15,
};

export function agentWorkflowRuntimeTargetLabel(target: AgentWorkflowRuntimeTarget): string {
  switch (target) {
    case "local-codex":
      return "Local Codex";
    case "cloud-codex-runner":
      return "Cloud Codex runner";
    case "managed-api":
      return "Managed API";
  }
}

export function agentWorkflowRuntimeTargetRunsOffline(target: AgentWorkflowRuntimeTarget): boolean {
  return target === "cloud-codex-runner" || target === "managed-api";
}

export const AGENT_WORKFLOW_TEMPLATES: ReadonlyArray<AgentWorkflowDefinition> = [
  {
    id: "lead-intake-follow-up",
    version: 1,
    name: "Lead intake follow-up",
    status: "draft",
    source: "template",
    naturalLanguageDefinition:
      "Every morning, review new leads in Werkflow, identify requests that have not received a response, draft a short follow-up, and create a follow-up task. Do not send customer-facing messages without approval.",
    compactSkillDefinition:
      "Review new leads. Draft follow-up for unanswered requests. Create internal task. Require approval before customer contact.",
    schedule: {
      kind: "cron",
      expression: "0 8 * * *",
      timezone: "Europe/Berlin",
      label: "Daily 08:00",
    },
    security: {
      ...DEFAULT_AGENT_WORKFLOW_SECURITY,
      allowedToolIds: ["leads.list", "leads.update"],
    },
    steps: [
      {
        id: "find-new-leads",
        kind: "werkflow-tool",
        title: "Find new leads",
        instruction: "List open leads and filter for unanswered requests.",
        toolId: "leads.list",
      },
      {
        id: "draft-response",
        kind: "agent",
        title: "Draft response",
        instruction: "Write a concise follow-up in the user's tone without sending it.",
      },
      {
        id: "approval-before-send",
        kind: "approval",
        title: "Request approval",
        instruction: "Ask the operator before any customer-facing message is sent.",
        approvalReason: "Customer-facing communication requires review.",
      },
    ],
    createdAt: "2026-06-20T00:00:00.000Z",
    updatedAt: "2026-06-20T00:00:00.000Z",
  },
  {
    id: "unpaid-invoice-chase",
    version: 1,
    name: "Unpaid invoice chase",
    status: "paused",
    source: "template",
    naturalLanguageDefinition:
      "On workdays, find overdue invoices, prepare a polite payment reminder, summarize risk and customer context, and wait for approval before sending anything.",
    compactSkillDefinition:
      "Find overdue invoices. Prepare reminder and risk summary. Wait for approval before customer contact.",
    schedule: {
      kind: "cron",
      expression: "0 9 * * 1-5",
      timezone: "Europe/Berlin",
      label: "Weekdays 09:00",
    },
    security: {
      ...DEFAULT_AGENT_WORKFLOW_SECURITY,
      allowedToolIds: ["billing.list"],
    },
    steps: [
      {
        id: "list-overdue-invoices",
        kind: "werkflow-tool",
        title: "List overdue invoices",
        instruction: "Find unpaid invoices past their due date.",
        toolId: "billing.list",
      },
      {
        id: "prepare-reminder",
        kind: "agent",
        title: "Prepare reminder",
        instruction: "Draft a polite payment reminder and summarize context.",
      },
      {
        id: "send-approval",
        kind: "approval",
        title: "Request send approval",
        instruction: "Wait for explicit operator approval before sending.",
        approvalReason: "Payment reminders are customer-facing.",
      },
    ],
    createdAt: "2026-06-20T00:00:00.000Z",
    updatedAt: "2026-06-20T00:00:00.000Z",
  },
  {
    id: "material-reorder-check",
    version: 1,
    name: "Material reorder check",
    status: "ready",
    source: "template",
    naturalLanguageDefinition:
      "Every Monday morning, review inventory notes and upcoming jobs, identify likely missing materials, and create an internal reorder task. Do not use sandboxed code.",
    compactSkillDefinition:
      "Review inventory and upcoming jobs. Identify missing materials. Create internal reorder task. No sandbox.",
    schedule: {
      kind: "cron",
      expression: "30 7 * * 1",
      timezone: "Europe/Berlin",
      label: "Monday 07:30",
    },
    security: {
      ...DEFAULT_AGENT_WORKFLOW_SECURITY,
      allowedToolIds: ["inventory.list", "projects.list"],
      approvalRequiredForCustomerFacingActions: true,
      approvalRequiredForWrites: false,
    },
    steps: [
      {
        id: "check-stock",
        kind: "werkflow-tool",
        title: "Check stock notes",
        instruction: "Inspect available inventory and reorder notes.",
        toolId: "inventory.list",
      },
      {
        id: "compare-jobs",
        kind: "werkflow-tool",
        title: "Compare open jobs",
        instruction: "Compare inventory against materials likely needed for upcoming jobs.",
        toolId: "projects.list",
      },
      {
        id: "create-task",
        kind: "agent",
        title: "Create reorder task",
        instruction: "Prepare an internal reorder task with item, reason, and urgency.",
      },
    ],
    createdAt: "2026-06-20T00:00:00.000Z",
    updatedAt: "2026-06-20T00:00:00.000Z",
  },
];

export const AGENT_WORKFLOW_SAMPLE_RUNS: ReadonlyArray<AgentWorkflowRunRecord> = [
  {
    id: "run-quote-digest",
    workflowId: "quote-digest",
    workflowVersion: 1,
    status: "completed",
    inputSummary: "Daily quote digest",
    resultSummary: "Prepared a summary of open quote follow-ups.",
    startedAt: "2026-06-20T08:00:00.000Z",
    completedAt: "2026-06-20T08:01:22.000Z",
    events: [],
  },
  {
    id: "run-invoice-chase",
    workflowId: "unpaid-invoice-chase",
    workflowVersion: 1,
    status: "waiting-for-approval",
    inputSummary: "Weekday overdue invoice check",
    resultSummary: "Payment reminder draft is waiting for approval.",
    startedAt: "2026-06-20T09:00:00.000Z",
    events: [],
  },
  {
    id: "run-lead-follow-up",
    workflowId: "lead-intake-follow-up",
    workflowVersion: 1,
    status: "queued",
    scheduledFor: "2026-06-21T08:00:00.000Z",
    inputSummary: "Next lead follow-up run",
    events: [],
  },
];

export function compactAgentWorkflowText(input: string): string {
  return input
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1");
}

export function createAgentWorkflowTenantStore(
  options: CreateAgentWorkflowTenantStoreOptions,
): AgentWorkflowTenantStore {
  return {
    tenantId: options.tenantId,
    schemaVersion: AGENT_WORKFLOW_TENANT_STORE_SCHEMA_VERSION,
    definitions: options.definitions ?? [],
    runs: options.runs ?? [],
    updatedAt: options.updatedAt,
  };
}

export function updateAgentWorkflowSchedule(
  workflow: AgentWorkflowDefinition,
  schedule: AgentWorkflowSchedule,
  updatedAt: string,
): AgentWorkflowDefinition {
  return {
    ...workflow,
    version: workflow.version + 1,
    schedule,
    updatedAt,
  };
}

function workflowNeedsApproval(workflow: AgentWorkflowDefinition): boolean {
  return (
    workflow.steps.some((step) => step.kind === "approval") ||
    workflow.security.approvalRequiredForCustomerFacingActions ||
    workflow.security.approvalRequiredForWrites
  );
}

export function createAgentWorkflowRunRecord(
  options: CreateAgentWorkflowRunRecordOptions,
): AgentWorkflowRunRecord {
  const requiresApproval = workflowNeedsApproval(options.workflow);
  const eventBase = `${options.id}:`;
  const events: AgentWorkflowRunEvent[] = [
    {
      id: `${eventBase}started`,
      at: options.startedAt,
      type: "started",
      message: `Started ${options.workflow.name} on ${options.workflow.security.runtimeTarget}.`,
    },
  ];

  for (const step of options.workflow.steps) {
    if (step.kind === "werkflow-tool") {
      events.push({
        id: `${eventBase}tool:${step.id}`,
        at: options.startedAt,
        type: "tool-call",
        message: step.toolId
          ? `Prepared approved Werkflow tool call: ${step.toolId}.`
          : `Prepared Werkflow tool step: ${step.title}.`,
      });
    }
    if (step.kind === "approval") {
      events.push({
        id: `${eventBase}approval:${step.id}`,
        at: options.startedAt,
        type: "approval-requested",
        message: step.approvalReason ?? "Workflow is waiting for operator approval.",
      });
    }
  }

  if (requiresApproval) {
    return {
      id: options.id,
      workflowId: options.workflow.id,
      workflowVersion: options.workflow.version,
      status: "waiting-for-approval",
      trigger: options.trigger,
      runtimeTarget: options.workflow.security.runtimeTarget,
      ...(options.scheduledFor ? { scheduledFor: options.scheduledFor } : {}),
      startedAt: options.startedAt,
      inputSummary: renderAgentWorkflowSkill(options.workflow),
      resultSummary: "Prepared workflow run and stopped at the approval boundary.",
      events,
    };
  }

  const completedAt = options.completedAt ?? options.startedAt;
  return {
    id: options.id,
    workflowId: options.workflow.id,
    workflowVersion: options.workflow.version,
    status: "completed",
    trigger: options.trigger,
    runtimeTarget: options.workflow.security.runtimeTarget,
    ...(options.scheduledFor ? { scheduledFor: options.scheduledFor } : {}),
    startedAt: options.startedAt,
    completedAt,
    inputSummary: renderAgentWorkflowSkill(options.workflow),
    resultSummary: "Workflow run completed on the configured Codex runtime.",
    events: [
      ...events,
      {
        id: `${eventBase}result`,
        at: completedAt,
        type: "result",
        message: "Workflow finished without requiring an approval gate.",
      },
    ],
  };
}

export function createQueuedAgentWorkflowRunRecord(options: {
  readonly id: string;
  readonly workflow: AgentWorkflowDefinition;
  readonly trigger: AgentWorkflowRunTrigger;
  readonly scheduledFor?: string;
  readonly queuedAt: string;
}): AgentWorkflowRunRecord {
  const event: AgentWorkflowRunEvent = {
    id: `${options.id}:queued`,
    at: options.queuedAt,
    type: "started",
    message: "Workflow queued for server execution.",
  };
  return {
    id: options.id,
    workflowId: options.workflow.id,
    workflowVersion: options.workflow.version,
    status: "queued",
    trigger: options.trigger,
    runtimeTarget: options.workflow.security.runtimeTarget,
    ...(options.scheduledFor === undefined ? {} : { scheduledFor: options.scheduledFor }),
    inputSummary: options.workflow.naturalLanguageDefinition,
    events: [event],
  };
}

export type AgentWorkflowApprovalDecision = "approve" | "decline";

export function resolveAgentWorkflowRunApproval(
  run: AgentWorkflowRunRecord,
  decision: AgentWorkflowApprovalDecision,
  at: string,
): AgentWorkflowRunRecord {
  if (run.status !== "waiting-for-approval") {
    return run;
  }
  const resolvedEvent: AgentWorkflowRunEvent = {
    id: `${run.id}:approval-resolved`,
    at,
    type: "approval-resolved",
    message:
      decision === "approve"
        ? "Operator approved the workflow at the approval boundary."
        : "Operator declined the workflow at the approval boundary.",
  };

  if (decision === "approve") {
    return {
      ...run,
      status: "completed",
      completedAt: at,
      resultSummary: "Workflow completed after operator approval.",
      events: [
        ...run.events,
        resolvedEvent,
        {
          id: `${run.id}:result`,
          at,
          type: "result",
          message: "Workflow finished after the approval gate was cleared.",
        },
      ],
    };
  }

  return {
    ...run,
    status: "cancelled",
    completedAt: at,
    resultSummary: "Workflow stopped after the operator declined the approval.",
    events: [...run.events, resolvedEvent],
  };
}

export function cancelAgentWorkflowRun(
  run: AgentWorkflowRunRecord,
  at: string,
): AgentWorkflowRunRecord {
  if (
    run.status !== "queued" &&
    run.status !== "running" &&
    run.status !== "waiting-for-approval"
  ) {
    return run;
  }
  return {
    ...run,
    status: "cancelled",
    completedAt: at,
    resultSummary: run.resultSummary ?? "Workflow run cancelled by operator.",
    events: [
      ...run.events,
      {
        id: `${run.id}:cancelled`,
        at,
        type: "error",
        message: "Workflow run cancelled by operator before completion.",
      },
    ],
  };
}

function padTimePart(value: number): string {
  return String(value).padStart(2, "0");
}

function parseTimeLabel(
  input: string,
  fallbackHour: number,
): {
  readonly hour: number;
  readonly minute: number;
  readonly label: string;
} {
  const match = /\b(?:at|um)\s*(\d{1,2})(?::|\.?(\d{2}))?\s*(am|pm)?\b/i.exec(input);
  if (!match) {
    return {
      hour: fallbackHour,
      minute: 0,
      label: `${padTimePart(fallbackHour)}:00`,
    };
  }

  const rawHour = Number(match[1]);
  const minute = Math.min(59, Math.max(0, Number(match[2] ?? 0)));
  const meridiem = match[3]?.toLowerCase();
  const hour =
    meridiem === "pm" && rawHour < 12
      ? rawHour + 12
      : meridiem === "am" && rawHour === 12
        ? 0
        : rawHour;
  const normalizedHour = Math.min(23, Math.max(0, hour));
  return {
    hour: normalizedHour,
    minute,
    label: `${padTimePart(normalizedHour)}:${padTimePart(minute)}`,
  };
}

const WEEKDAY_BY_NAME: Readonly<Record<string, number>> = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
};

function sentenceCase(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "Workflow";
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

function stripWorkflowCommandPrefix(input: string): string {
  return input
    .trim()
    .replace(/^\/workflow\b\s*/i, "")
    .replace(
      /^(?:please\s+)?(?:create|add|new|set up|setup)\s+(?:a\s+)?workflow\s*(?:to|that|for)?\s*/i,
      "",
    )
    .replace(
      /^(?:please\s+)?(?:schedule|reschedule|update|change)\s+(?:the\s+)?(?:workflow\s*)?/i,
      "",
    )
    .trim();
}

function stripLeadingScheduleClause(input: string): string {
  return input
    .replace(
      /^(?:every\s+(?:weekday|workday|day|morning|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|daily|weekdays|workdays)(?:\s+at\s+\d{1,2}(?::|\.?\d{2})?\s*(?:am|pm)?)?\s*,?\s*/i,
      "",
    )
    .trim();
}

function deriveAgentWorkflowName(input: string): string {
  const withoutPrefix = stripWorkflowCommandPrefix(input);
  const withoutSchedule = stripLeadingScheduleClause(withoutPrefix)
    .replace(/\b(?:every|daily|weekdays?|workdays?)\b.*$/i, "")
    .replace(/\b(?:for approval|with approval|after approval)\b.*$/i, "")
    .trim();
  const source = withoutSchedule || withoutPrefix || input;
  const words = source
    .replace(/[^\w\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 8)
    .join(" ");
  return sentenceCase(words || "Agent workflow");
}

export function parseAgentWorkflowSchedule(input: string): AgentWorkflowSchedule | null {
  const text = input.trim();
  const lower = text.toLowerCase();
  if (!text) return null;
  if (/\bmanual(?:ly)?\b/.test(lower)) {
    return { kind: "manual", label: "Manual" };
  }

  const intervalMatch = /\bevery\s+(\d{1,3})\s+(minute|minutes|hour|hours|day|days)\b/i.exec(text);
  if (intervalMatch) {
    const amount = Math.max(1, Number(intervalMatch[1]));
    const unit = intervalMatch[2]!.toLowerCase();
    const designator = unit.startsWith("minute") ? "M" : unit.startsWith("hour") ? "H" : "D";
    const singular = amount === 1 ? unit.replace(/s$/, "") : unit;
    return {
      kind: "interval",
      expression: `PT${amount}${designator}`,
      label: `Every ${amount} ${singular}`,
    };
  }

  if (/\b(?:weekday|weekdays|workday|workdays)\b/.test(lower)) {
    const time = parseTimeLabel(text, 9);
    return {
      kind: "cron",
      expression: `${time.minute} ${time.hour} * * 1-5`,
      timezone: "Europe/Berlin",
      label: `Weekdays ${time.label}`,
    };
  }

  for (const [dayName, dayNumber] of Object.entries(WEEKDAY_BY_NAME)) {
    if (new RegExp(`\\b${dayName}\\b`, "i").test(text)) {
      const time = parseTimeLabel(text, 8);
      return {
        kind: "cron",
        expression: `${time.minute} ${time.hour} * * ${dayNumber}`,
        timezone: "Europe/Berlin",
        label: `${sentenceCase(dayName)} ${time.label}`,
      };
    }
  }

  if (/\b(?:daily|every day|each day|every morning|morning)\b/.test(lower)) {
    const time = parseTimeLabel(text, 8);
    return {
      kind: "cron",
      expression: `${time.minute} ${time.hour} * * *`,
      timezone: "Europe/Berlin",
      label: `Daily ${time.label}`,
    };
  }

  return null;
}

export function parseAgentWorkflowChatCommand(input: string): AgentWorkflowChatCommand | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const explicitWorkflowCommand =
    /^\/workflow\b/i.test(trimmed) ||
    /^(?:please\s+)?(?:create|add|new|set up|setup)\s+(?:a\s+)?workflow\b/i.test(trimmed) ||
    /^(?:please\s+)?(?:schedule|reschedule|update|change)\s+(?:the\s+)?workflow\b/i.test(trimmed);
  const schedule = parseAgentWorkflowSchedule(trimmed);
  const naturalSchedulingCommand =
    schedule !== null &&
    /^(?:please\s+)?(?:every|daily|weekdays?|workdays?|schedule|run|check|review|prepare|chase|follow up|automate)\b/i.test(
      trimmed,
    ) &&
    !/^(?:what|why|how|can|could|would|should)\b/i.test(trimmed);

  if (!explicitWorkflowCommand && !naturalSchedulingCommand) {
    return null;
  }

  const actionHint =
    /^(?:\/workflow\s+)?(?:schedule|reschedule|update|change)\b/i.test(trimmed) ||
    /\b(?:reschedule|change|update)\b/i.test(trimmed)
      ? "reschedule"
      : "create";
  const definition = stripWorkflowCommandPrefix(trimmed);

  return {
    actionHint,
    naturalLanguageDefinition: compactAgentWorkflowText(definition || trimmed),
    name: deriveAgentWorkflowName(trimmed),
    schedule: schedule ?? { kind: "manual", label: "Manual" },
  };
}

function inferWorkflowToolIds(input: string): ReadonlyArray<string> {
  const lower = input.toLowerCase();
  const tools = new Set<string>();
  if (/\b(?:lead|leads|inquiry|inquiries)\b/.test(lower)) tools.add("leads.list");
  if (/\b(?:invoice|invoices|payment|payments|billing|overdue)\b/.test(lower)) {
    tools.add("billing.list");
  }
  if (/\b(?:inventory|material|materials|stock|reorder)\b/.test(lower)) {
    tools.add("inventory.list");
  }
  if (/\b(?:job|jobs|project|projects|appointment|appointments|schedule)\b/.test(lower)) {
    tools.add("projects.list");
  }
  return [...tools];
}

export interface CreateAgentWorkflowDefinitionOptions {
  readonly id: string;
  readonly name: string;
  readonly naturalLanguageDefinition: string;
  readonly schedule: AgentWorkflowSchedule;
  readonly createdAt: string;
}

/**
 * Build a full {@link AgentWorkflowDefinition} from explicit, already-validated
 * fields (name, instruction, schedule). This is the shared construction path
 * used by both the chat-command flow and the explicit "create" form so the two
 * produce identical workflow shapes without depending on NL command parsing.
 */
export function createAgentWorkflowDefinition(
  options: CreateAgentWorkflowDefinitionOptions,
): AgentWorkflowDefinition {
  const naturalLanguageDefinition = compactAgentWorkflowText(options.naturalLanguageDefinition);
  const wantsConvex = /\b(?:convex|deterministic|automation|automations?)\b/i.test(
    naturalLanguageDefinition,
  );
  const toolIds = inferWorkflowToolIds(naturalLanguageDefinition);
  const steps: AgentWorkflowStepDefinition[] = [];
  const primaryToolId = toolIds[0];
  if (primaryToolId) {
    steps.push({
      id: "collect-erp-context",
      kind: "werkflow-tool",
      title: "Collect ERP context",
      instruction: "Use approved Werkflow tools to gather the records needed for this workflow.",
      toolId: primaryToolId,
    });
  }
  steps.push(
    {
      id: "agent-work",
      kind: "agent",
      title: "Run agent workflow",
      instruction: naturalLanguageDefinition,
    },
    {
      id: "approval-gate",
      kind: "approval",
      title: "Request approval",
      instruction: "Ask the operator before writes or customer-facing actions are executed.",
      approvalReason: "Agent-created workflows require approval for writes and customer contact.",
    },
  );

  return {
    id: options.id,
    version: 1,
    name: options.name,
    status: "ready",
    source: "chat",
    naturalLanguageDefinition,
    compactSkillDefinition: compactAgentWorkflowText(naturalLanguageDefinition),
    schedule: options.schedule,
    security: {
      ...DEFAULT_AGENT_WORKFLOW_SECURITY,
      allowedToolIds: toolIds,
    },
    executionKind: wantsConvex ? "convex" : "agent",
    ...(wantsConvex
      ? {
          convex: {
            deploymentRef: "",
            entrypoint: "agentWorkflow",
            sourceVersion: 1,
            deploymentStatus: "draft" as const,
            declaredCapabilities: toolIds,
            declaredSideEffects: [],
          },
        }
      : {}),
    steps,
    createdAt: options.createdAt,
    updatedAt: options.createdAt,
  };
}

export function createAgentWorkflowDefinitionFromChatCommand(
  options: CreateAgentWorkflowFromChatCommandOptions,
): AgentWorkflowDefinition {
  return createAgentWorkflowDefinition({
    id: options.id,
    name: options.command.name,
    naturalLanguageDefinition: options.command.naturalLanguageDefinition,
    schedule: options.command.schedule,
    createdAt: options.createdAt,
  });
}

export function renderAgentWorkflowSkill(
  workflow: AgentWorkflowDefinition,
  options: AgentWorkflowSkillRenderOptions = {},
): string {
  const lines = [
    `# ${workflow.name}`,
    `Status: ${workflow.status}`,
    `Runtime: ${workflow.security.runtimeTarget}`,
    `Security: sandboxAllowed=${workflow.security.sandboxAllowed}; approvals=writes:${workflow.security.approvalRequiredForWrites}, customer:${workflow.security.approvalRequiredForCustomerFacingActions}`,
    `Tools: ${workflow.security.allowedToolIds.length > 0 ? workflow.security.allowedToolIds.join(", ") : "none"}`,
  ];

  if (options.includeSchedule !== false) {
    lines.push(`Schedule: ${workflow.schedule.label}`);
  }

  lines.push(
    "",
    "## Instruction",
    compactAgentWorkflowText(workflow.compactSkillDefinition),
    "",
    "## Steps",
    ...workflow.steps.map((step, index) => `${index + 1}. ${step.title}: ${step.instruction}`),
  );

  return lines.join("\n");
}

export function workflowRequiresSandbox(workflow: AgentWorkflowDefinition): boolean {
  return workflow.steps.some((step) => step.kind === "sandbox");
}

export function assertAgentWorkflowSecurity(workflow: AgentWorkflowDefinition): void {
  if (
    workflow.security.runtimeTarget !== "local-codex" &&
    workflow.security.runtimeTarget !== "cloud-codex-runner" &&
    workflow.security.runtimeTarget !== "managed-api"
  ) {
    throw new Error(`Workflow has an unsupported runtime target: ${workflow.id}`);
  }
  if (workflowRequiresSandbox(workflow) && !workflow.security.sandboxAllowed) {
    throw new Error(`Workflow uses sandbox steps but sandbox is disabled: ${workflow.id}`);
  }
  if (!Number.isFinite(workflow.security.maxRunMinutes) || workflow.security.maxRunMinutes <= 0) {
    throw new Error(`Workflow maxRunMinutes must be positive: ${workflow.id}`);
  }
}
